import store from 'store';

import user from 'js/lib/user';

import type { PlaybackRate, ResolutionOption } from 'bundles/video-player/types/VideoPlayer';

type VideoPlayerUserPreferenceValues = {
  playbackRate: PlaybackRate;
  volume: number;
  subtitleLanguage: string;
  autoplay: boolean;
  key: string;
  audioLanguage: string;
  dubbedSubtitleLanguage: string;
  resolution?: ResolutionOption;
  hlsResolution?: string;
};

const getDefaults = (): VideoPlayerUserPreferenceValues => {
  return {
    playbackRate: 1,
    volume: 1,
    subtitleLanguage: 'none',
    // We decided to keep autoplay off by default after the results of this experiment: https://tools.coursera.org/epic/experiment/s0B8NJw5Ee2-GQ4eycwoYw
    // See also https://coursera.atlassian.net/wiki/spaces/CLE/pages/2954428417/2023-02-07+Autoplay+location+and+default?focusedCommentId=3065643045
    autoplay: false,
    hlsResolution: 'auto',
    key: 'userPreferences',
    audioLanguage: 'none',
    dubbedSubtitleLanguage: 'none',
  };
};

export class UserPreferences {
  ensureAndGetStoredPreferences = () => {
    const oldPreferences: VideoPlayerUserPreferenceValues = this.getStoredPreferences();
    const defaults = getDefaults();

    if (!oldPreferences) {
      store.set(this.makeStorageKey(), defaults);
    }

    return this.getStoredPreferences();
  };

  makeStorageKey = () => `${user.get().id}.userPreferences`;

  getStoredPreferences = () => store.get(this.makeStorageKey());

  get(key: keyof VideoPlayerUserPreferenceValues) {
    const preferences = this.ensureAndGetStoredPreferences();
    return preferences[key];
  }

  set(key: keyof VideoPlayerUserPreferenceValues, value: string | boolean | number) {
    const oldPreferences = this.ensureAndGetStoredPreferences();
    const newPreferences = { ...oldPreferences, [key]: value };
    store.set(this.makeStorageKey(), newPreferences);
  }
}

// Exports a singleton instance of the class.
// Future enhancement: Either don't export the singleton, or rename this file to userPreferencesSingleton.ts.
export default new UserPreferences();
